import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import {
  PublicKey,
  Transaction,
  TransactionMessage,
  VersionedTransaction,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import BigNumber from "bignumber.js";
import { A1X_SOLANA_TOKEN, A1X_SOLANA_DECIMALS } from "@/config/sharedConfig";

interface Params {
  toAddress: string;
  amount: string;
}

export const useSolanaTransaction = () => {
  const wallet = useWallet();
  const { connection } = useConnection();

  const sendTransaction = useCallback(
    async ({ toAddress, amount }: Params) => {
      if (!wallet.publicKey || !wallet.signTransaction)
        throw new Error("Wallet not connected");

      const payer = wallet.publicKey;
      const recipient = new PublicKey(toAddress);
      const mint = new PublicKey(A1X_SOLANA_TOKEN);

      /* ── read mint owner to know which token program to use ─────────── */
      const mintInfo = await connection.getAccountInfo(mint);
      if (!mintInfo) throw new Error("Mint account not found on chain");
      const tokenProgramId = mintInfo.owner; // could be Token‑2022

      /* ── derive ATAs under that program id ───────────────────────────── */
      const senderAta = await getAssociatedTokenAddress(
        mint,
        payer,
        false,
        tokenProgramId,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      const recipientAta = await getAssociatedTokenAddress(
        mint,
        recipient,
        false,
        tokenProgramId,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const legacyTx = new Transaction();

      /* create recipient ATA if missing */
      if (!(await connection.getAccountInfo(recipientAta))) {
        const rent = await connection.getMinimumBalanceForRentExemption(165);
        const balance = await connection.getBalance(payer);
        if (balance < rent + 0.0005 * LAMPORTS_PER_SOL)
          throw new Error("Not enough SOL to create recipient ATA");

        legacyTx.add(
          createAssociatedTokenAccountInstruction(
            payer,
            recipientAta,
            recipient,
            mint,
            tokenProgramId, // <‑‑ correct program here
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      /* transfer → use checked variant with same program id */
      const raw = BigInt(
        new BigNumber(amount)
          .multipliedBy(10 ** A1X_SOLANA_DECIMALS)
          .integerValue(BigNumber.ROUND_DOWN)
          .toFixed(0)
      );
      legacyTx.add(
        createTransferCheckedInstruction(
          senderAta,
          mint,
          recipientAta,
          payer,
          raw,
          A1X_SOLANA_DECIMALS,
          [],
          tokenProgramId // <‑‑ correct program here
        )
      );

      /* compile to v0 message */
      const { blockhash } = await connection.getLatestBlockhash();
      const msg = new TransactionMessage({
        payerKey: payer,
        recentBlockhash: blockhash,
        instructions: legacyTx.instructions,
      }).compileToV0Message();
      const v0Tx = new VersionedTransaction(msg);

      /* simulate (no sigVerify) */
      const sim = await connection.simulateTransaction(v0Tx, {
        replaceRecentBlockhash: true,
      });
      if (sim.value.err) {
        const logs = sim.value.logs?.join("\n") ?? "no logs";
        throw new Error(`Simulation failed:\n${logs}`);
      }

      /* sign & send */
      const signed = await wallet.signTransaction(v0Tx);
      const sig = await connection.sendRawTransaction(signed.serialize(), {
        preflightCommitment: "confirmed",
      });
      
      // Return immediately after sending, don't wait for confirmation
      // This allows the loading state to disappear after transaction is sent
      return { signature: sig };
    },
    [wallet, connection]
  );

  const mutation = useMutation({
    mutationFn: sendTransaction,
    onError: (e) => console.error("Solana transaction error:", e),
  });

  return {
    sendTransaction: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
